import React, { useState } from 'react';
import PlacesAutocomplete from 'react-places-autocomplete';
import { TextField, InputAdornment, IconButton, Chip, Box } from '@mui/material';
import ClearIcon from '@mui/icons-material/Clear';
import { isEmpty } from '../../utils/lodash';
import { ILocation, LocationOption } from '../../types/global/helper';
import CheckboxGroup from '../CheckboxGroup/CheckboxGroup';
import { FONT_ENUM } from '../../types/global/fonts.types';
import { SentryCapture } from '../../analytics/Sentry';
import { showToastMessage } from '../../utils/AppUtils';
import { geocodeByAddress, getLatLng } from 'react-places-autocomplete';
import { theme } from '../../constants/theme';
import { useThemeContext } from '../../hook/useThemeManager';
import { Spinner } from '@chakra-ui/react';

type Loc = {
    address: string;
    description: string;
    coordinates: number[];
};
interface LocationAutocompleteProps {
    value: Loc | Loc[];
    locationOptions?: LocationOption[];
    onSelect: (address: string, coordinates?: number[], formattedAddress?: ILocation) => void;
    onSelectLocationOption: (address: LocationOption | null) => void;
    onDelete?: (index?: number) => void;
    placeholder?: string;
    label?: string;
    withCordinates?: boolean;
    fieldStyle?: {
        bgcolor: string;
        borderRadius: string;
        input: { color: string };
        marginTop: string;
        label: { color: string };
        '& .MuiOutlinedInput-notchedOutline': { borderColor: string };
        '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: string };
        '& .Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: string };
    };
    searchOptions?: {
        bounds?: google.maps.LatLngBounds | google.maps.LatLngBoundsLiteral | undefined;
        componentRestrictions?: google.maps.places.ComponentRestrictions | undefined;
        location?: google.maps.LatLng | google.maps.LatLngLiteral | undefined;
        offset?: number | string | undefined;
        radius?: number | string | undefined;
        sessionToken?: any;
        types?: string[] | undefined;
        locationRestriction?: google.maps.LatLngBounds | google.maps.LatLngBoundsLiteral | undefined;
    };
}

const LocationAutocomplete: React.FC<LocationAutocompleteProps> = ({
    value,
    onSelect,
    placeholder = 'Search Location ...',
    fieldStyle,
    onDelete,
    locationOptions,
    onSelectLocationOption,
    searchOptions,
    withCordinates,
}) => {
    const [text, setText] = useState('');
    const { themeStyle } = useThemeContext();

    return (
        <>
            <PlacesAutocomplete
                value={text}
                onChange={text => setText(text)}
                onError={(error, clearSuggestion) => {
                    showToastMessage(error, 'warning');
                    SentryCapture(error);
                    clearSuggestion();
                }}
                onSelect={address => {
                    let formattedAddress = '';
                    if (withCordinates) {
                        geocodeByAddress(address)
                            .then(results => {
                                formattedAddress = results[0].formatted_address;
                                return getLatLng(results[0]);
                            })
                            .then(latLng => {
                                const locationData = {
                                    type: 'Point',
                                    coordinates: [latLng.lng, latLng.lat],
                                    address: formattedAddress,
                                    description: formattedAddress,
                                    short_name: formattedAddress.split(',')[0],
                                };
                                onSelect(address, [latLng.lat, latLng.lng], locationData);
                            })
                            .catch(error => console.error('Error', error));
                    } else {
                        onSelect(address);
                    }
                    setText('');
                }}
                searchOptions={searchOptions}>
                {({ getInputProps, suggestions, getSuggestionItemProps, loading }) => (
                    <div style={{ position: 'relative' }}>
                        <TextField
                            fullWidth
                            margin="normal"
                            {...getInputProps({
                                placeholder,
                            })}
                            sx={{
                                ...fieldStyle,
                                // TextInput Styles
                                bgcolor: theme[themeStyle].deep_grey_lighter,
                                '& .MuiOutlinedInput-root': {
                                    '& fieldset': {
                                        borderColor: theme[themeStyle].tag_white, // Default border color
                                    },
                                    '&:hover fieldset': {
                                        borderColor: theme[themeStyle].highlight, // Change on hover
                                    },
                                    '&.Mui-focused': {
                                        borderColor: theme[themeStyle].highlight, // Change when focused
                                    },
                                },
                                '& .MuiInputBase-input': {
                                    fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                    fontSize: 12,
                                    color: theme[themeStyle].tag_white,
                                },
                            }}
                            InputProps={{
                                endAdornment: text ? (
                                    <InputAdornment position="end">
                                        <IconButton onClick={() => setText('')} edge="end">
                                            <ClearIcon sx={{ color: theme[themeStyle].tag_white }} />
                                        </IconButton>
                                    </InputAdornment>
                                ) : null,
                                style: {
                                    fontFamily: FONT_ENUM.MAIN_MEDIUM, // Replace with FONT_ENUM if needed
                                    fontSize: 12,
                                    color: theme[themeStyle].tag_white,
                                    borderColor: theme[themeStyle].tag_white,
                                },
                            }}
                        />
                        <div
                            style={{
                                position: 'absolute',
                                zIndex: 10,
                                backgroundColor: theme[themeStyle].secondaryBg,
                                boxShadow: '0px 4px 6px rgba(0, 0, 0, 0.1)',
                                borderRadius: '8px',
                                marginTop: '4px',
                                width: '100%',
                            }}>
                            {loading && (
                                <Box
                                    sx={{
                                        width: '100%',
                                        display: 'flex',
                                        justifyContent: 'center',
                                        alignItems: 'center',
                                    }}>
                                    <Spinner h={2} w={2} color={theme[themeStyle].tag_white} />
                                </Box>
                            )}
                            {suggestions.map(suggestion => {
                                const style = {
                                    backgroundColor: suggestion.active
                                        ? theme[themeStyle].deep_grey
                                        : theme[themeStyle].secondaryBg,
                                    cursor: 'pointer',
                                    padding: '10px',
                                    color: theme[themeStyle].tag_white,
                                };
                                return (
                                    <div
                                        {...getSuggestionItemProps(suggestion, {
                                            style,
                                        })}
                                        key={suggestion.placeId}>
                                        {suggestion.description}
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )}
            </PlacesAutocomplete>
            {Array.isArray(value)
                ? value.map((value, index) => (
                      <Chip
                          key={index}
                          label={value.address}
                          variant="outlined"
                          onDelete={() => onDelete && onDelete(index)}
                          sx={{
                              color: theme[themeStyle].tag_white,
                              '& .MuiChip-deleteIcon': { color: theme[themeStyle].tag_white }, // Change color to red
                          }}
                      />
                  ))
                : !isEmpty(value) && (
                      <Chip
                          label={value.address}
                          sx={{
                              mr: 0.5,
                              color: theme[themeStyle].tag_white,
                              '& .MuiChip-deleteIcon': { color: theme[themeStyle].tag_white },
                          }}
                          variant="outlined"
                          onDelete={onDelete}
                      />
                  )}
            {locationOptions && isEmpty(value) && isEmpty(text) && (
                <CheckboxGroup options={locationOptions} onChange={value => onSelectLocationOption(value)} />
            )}
        </>
    );
};

export default LocationAutocomplete;
